/**
 * Minha conta (`api/v2/account/overview` e `api/v2/account/profile`): pagamentos com as notas
 * fiscais e o perfil do negócio. Sem a rota no ar, a página mostra só a licença e avisa.
 */
import { api } from '../lib/apiClient';
import type { ApiResult } from '../types/api';

export interface AccountPurchase {
  id: string;
  date: string | null;
  status: 'paid' | 'refunded';
  refundedAt: string | null;
  method: string | null;
  /** Em reais. */
  amount: number | null;
  currency: string;
  installments: number | null;
  products: string[];
  bonus: number;
  /** Período do código de ativação da compra, em dias. */
  days: number | null;
  invoice: {
    pdfUrl: string;
    xmlUrl: string | null;
    issuedAt: string | null;
    canceled: boolean;
  } | null;
}

export interface AccountProfile {
  /** Chave antiga do setor (`crm.industry`). */
  industry: string | null;
  /** Chave antiga do tipo de cliente (`crm.customer`): companies, professionals, people, both. */
  customer: string | null;
  segment: string | null;
  reach: string | null;
  place: { city: string | null; region: string | null; country: string | null } | null;
}

export interface AccountOverview {
  purchases: AccountPurchase[];
  profile: AccountProfile;
}

export const accountOverviewKey = (userId: string) => ['account-overview', userId] as const;

export function fetchAccountOverview(): Promise<ApiResult<AccountOverview>> {
  return api.get<AccountOverview>('api/v2/account/overview');
}

export function saveAccountProfile(profile: {
  industry?: string;
  customer?: string;
  segment?: string;
  reach?: string;
}): Promise<ApiResult<{ profile: AccountProfile }>> {
  return api.put<{ profile: AccountProfile }>('api/v2/account/profile', profile);
}

/**
 * Renovação automática do checkout novo (`api/v2/sales/subscriptions`, decisão 354): as
 * assinaturas da conta (pelo email e pela marca) e o cancelamento. Cancelar para as cobranças
 * seguintes; o período já pago continua valendo.
 */
export interface SaleSubscription {
  id: string;
  /** pending (primeiro pagamento), active, past_due (cobrança vencida), canceled, refused, expired, failed. */
  status: string;
  brand: string;
  /** card ou pix_automatic. */
  method: string | null;
  /** Ciclo da renovação, em meses: 1, 3, 6 ou 12. */
  months: number | null;
  currency: string;
  /** Em centavos. */
  total: number;
  items: { offer: string; amount: number }[];
  lastPaidAt: string | null;
  canceledAt: string | null;
}

export const subscriptionsKey = (userId: string) => ['sales-subscriptions', userId] as const;

export function fetchSubscriptions(): Promise<ApiResult<{ subscriptions: SaleSubscription[] }>> {
  return api.get<{ subscriptions: SaleSubscription[] }>('api/v2/sales/subscriptions');
}

export function cancelSubscription(
  id: string,
): Promise<ApiResult<{ subscription: SaleSubscription }>> {
  return api.post<{ subscription: SaleSubscription }>(
    `api/v2/sales/subscriptions/${encodeURIComponent(id)}/cancel`,
  );
}
